﻿import React, { createContext, useContext, useEffect, useReducer, useRef, ReactNode } from 'react';
import { BreedMateNotification } from '../api/Notification';
import { useGetNotifications, useUpdateNotification } from './api/NotificationQueries';
import { AxiosError } from 'axios';
import { MemberContext } from './MemberContext';
import { DateTime } from 'luxon';

enum BreedMateNotificationType {
  New = 0,
  Read = 1,
  Hidden = 2,
}

export interface NotificationState {
  notifications: BreedMateNotification[];
  hasNew: boolean;
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  lastUpdate: number | null;
}

interface SSENotificationData {
  type: BreedMateNotificationType;
  notification?: BreedMateNotification;
  unreadCount?: number;
  timestamp: number;
}

export type NotificationAction =
  | { type: 'SET_NOTIFICATIONS'; payload: BreedMateNotification[] }
  | { type: 'UPDATE_FROM_SSE'; payload: SSENotificationData }
  | { type: 'MARK_AS_READ'; payload: number }
  | { type: 'MARK_AS_HIDDEN'; payload: number }
  | { type: 'SET_CONNECTION_STATUS'; payload: boolean }
  | { type: 'SET_NEW_STATUS'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

interface NotificationContextType extends NotificationState {
  markAsRead: (notificationId: number) => Promise<void>;
  markAsHidden: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  clearNewStatus: (includeNotifications?: boolean) => void;
  reconnect: () => void;
}

interface NotificationProviderProps {
  children: ReactNode;
}

const initialState: NotificationContextType = {
  notifications: [],
  unreadCount: 0,
  hasNew: false,
  isConnected: false,
  isLoading: false,
  error: null,
  lastUpdate: null,
  fetchNotifications: () => Promise.resolve(),
  markAllAsRead: () => Promise.resolve(),
  markAsHidden: (notificationId: number) => Promise.resolve(),
  markAsRead: (notificationId: number) => Promise.resolve(),
  clearNewStatus: (includeNotifications: boolean = false) => {},
  reconnect: () => {},
};

const NotificationContext = createContext<NotificationContextType>(initialState);

export const notificationReducer = (
  state: NotificationState,
  action: NotificationAction
): NotificationState => {
  switch (action.type) {
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter((n) => !n.isRead).length,
        isLoading: false,
      };
    case 'UPDATE_FROM_SSE': {
      const updatedNotifications = action.payload.notification
        ? (() => {
            const newNotification = action.payload.notification;
            newNotification!.isNew = true;
            const existingIndex = state.notifications.findIndex((n) => n.id === newNotification.id);
            let updatedNotifications;

            if (existingIndex !== -1) {
              // Replace existing notification with same ID
              updatedNotifications = [...state.notifications];
              updatedNotifications[existingIndex] = newNotification;
            } else {
              // Add new notification
              updatedNotifications = [...state.notifications, newNotification];
            }

            // Sort by modifiedDate (newest first) then my createdDate
            return updatedNotifications.sort((a, b) => {
              let dateA = DateTime.fromJSDate(a.modifiedDate);
              let dateB = DateTime.fromJSDate(b.modifiedDate);
              if (dateA.equals(dateB)) {
                dateA = DateTime.fromJSDate(a.createdDate);
                dateB = DateTime.fromJSDate(b.createdDate);
              }

              return dateB.toMillis() - dateA.toMillis();
            });
          })()
        : state.notifications;
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter((n) => !n.isRead).length,
        lastUpdate: action.payload.timestamp,
        hasNew: true,
      };
    }
    case 'MARK_AS_READ': {
      const updatedNotifications = state.notifications.map((n) =>
        n.id === action.payload ? { ...n, isRead: true } : n
      );
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter((n) => !n.isRead).length,
      };
    }
    case 'MARK_AS_HIDDEN': {
      const visibleNotifications = state.notifications.filter((n) => n.id !== action.payload);
      return {
        ...state,
        notifications: visibleNotifications,
        unreadCount: visibleNotifications.filter((n) => !n.isRead).length,
      };
    }
    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        isConnected: action.payload,
      };
    case 'SET_NEW_STATUS':
      return {
        ...state,
        hasNew: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

function NotificationProvider({ children }: NotificationProviderProps) {
  const { isLoggedIn } = useContext(MemberContext);
  const { refetch } = useGetNotifications(false);
  const { mutateAsync: updateNotification } = useUpdateNotification();
  const [state, dispatch] = useReducer(notificationReducer, {
    notifications: [],
    unreadCount: 0,
    hasNew: false,
    isConnected: false,
    isLoading: false,
    error: null,
    lastUpdate: null,
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttempts = useRef<number>(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    // Only fetch notifications and setup SSE connection when member is logged in
    if (isLoggedIn) {
      // Initial load of notifications
      fetchNotifications();

      // Setup SSE connection
      connectSSE();
    }

    // Cleanup on unmount or when login status changes
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isLoggedIn]);

  const fetchNotifications = async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const { data: notifications, error } = await refetch();
    dispatch({ type: 'SET_NOTIFICATIONS', payload: notifications ?? [] });
    dispatch({ type: 'SET_ERROR', payload: error?.message ?? null });
  };

  const connectSSE = (): void => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const eventSource = new EventSource('/api/notification/sse-stream');
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('connected', (event: MessageEvent) => {
        console.log('SSE Connected:', JSON.parse(event.data));
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
        reconnectAttempts.current = 0; // Reset reconnect attempts on successful connection
      });

      eventSource.addEventListener('notification-update', (event: MessageEvent) => {
        const rawData: SSENotificationData = JSON.parse(event.data);
        // Transform date strings to Date objects if notification exists
        const data: SSENotificationData = {
          ...rawData,
          notification: rawData.notification
            ? {
                ...rawData.notification,
                createdDate: new Date(rawData.notification.createdDate),
                modifiedDate: new Date(rawData.notification.modifiedDate),
              }
            : undefined,
        };

        console.log('Notification update received:', data);

        if (data.type === BreedMateNotificationType.New) {
          dispatch({ type: 'UPDATE_FROM_SSE', payload: data });
          return;
        } else if (data.type === BreedMateNotificationType.Read) {
          dispatch({ type: 'MARK_AS_READ', payload: data.notification!.id });
        } else if (data.type === BreedMateNotificationType.Hidden) {
          dispatch({ type: 'MARK_AS_HIDDEN', payload: data.notification!.id });
        }

        /*// Show browser notification for new notifications if page is not active
        if (data.type && !document.hidden) {
          showBrowserNotification(data);
        }*/
      });

      eventSource.addEventListener('heartbeat', (event: MessageEvent) => {
        // Just keep the connection alive
        console.log('SSE Heartbeat received');
      });

      eventSource.onerror = (event: Event) => {
        console.error('SSE error:', event);
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connectSSE();
          }, delay);
        } else {
          console.error('Max reconnection attempts reached');
          dispatch({ type: 'SET_ERROR', payload: 'Connection lost. Please refresh the page.' });
        }
      };
    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
      dispatch({ type: 'SET_ERROR', payload: 'Failed to establish real-time connection' });
    }
  };

  const markAsRead = async (notificationId: number): Promise<void> => {
    try {
      const notification = state.notifications.find((n) => n.id === notificationId);
      if (notification) {
        await updateNotification({
          id: notificationId,
          notification: { ...notification, isRead: true },
        });
        dispatch({ type: 'MARK_AS_READ', payload: notificationId });
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Optionally show user-friendly error message
    }
  };

  const markAsHidden = async (notificationId: number): Promise<void> => {
    try {
      const notification = state.notifications.find((n) => n.id === notificationId);
      if (notification) {
        await updateNotification({
          id: notificationId,
          notification: { ...notification, isHidden: true },
        });
        dispatch({ type: 'MARK_AS_HIDDEN', payload: notificationId });
      }
    } catch (error) {
      console.error('Failed to hide notification:', error);
    }
  };

  const markAllAsRead = async (): Promise<void> => {
    try {
      const unreadNotifications = state.notifications.filter((n) => !n.isRead);

      // Mark all unread notifications as read
      const promises = unreadNotifications.map((notification) =>
        updateNotification({
          id: notification.id,
          notification: { ...notification, isRead: true },
        })
      );

      await Promise.all(promises);

      // Update local state
      const updatedNotifications = state.notifications.map((n) => ({ ...n, isRead: true }));
      dispatch({ type: 'SET_NOTIFICATIONS', payload: updatedNotifications });
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  /* const showBrowserNotification = (data: SSENotificationData): void => {
    // Request permission if not already granted
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      const title = getNotificationTitle(data.type);
      const body = data.data?.message || 'You have a new notification';

      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: data.type, // This prevents duplicate notifications
        requireInteraction: data.type?.includes('action_required'), // Keep important notifications open
      });

      // Auto-close after 5 seconds for non-critical notifications
      if (!data.type?.includes('action_required')) {
        setTimeout(() => notification.close(), 5000);
      }

      // Optional: Handle notification clicks
      notification.onclick = () => {
        window.focus();
        notification.close();
        // You could also navigate to a specific page based on notification type
        if (data.data?.transferId) {
          // Navigate to transfer page
          // window.location.href = `/transfers/${data.data.transferId}`;
        }
      };
    }
  };*/

  const reconnect = (): void => {
    reconnectAttempts.current = 0;
    dispatch({ type: 'SET_ERROR', payload: null });
    connectSSE();
  };

  const clearNewStatus = (includeNotifications: boolean = false) => {
    if (includeNotifications) {
      const updateNotifications = state.notifications.map((n) => {
        n.isNew = false;
        return n;
      });
      dispatch({ type: 'SET_NOTIFICATIONS', payload: updateNotifications });
    }
    dispatch({ type: 'SET_NEW_STATUS', payload: false });
  };

  const contextValue: NotificationContextType = {
    ...state,
    markAsRead,
    markAsHidden,
    markAllAsRead,
    fetchNotifications,
    reconnect,
    clearNewStatus,
  };

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
}

export { NotificationProvider, NotificationContext };
